import { Material, MaterialGroup, PartItem } from '@cutr/constants/cutlist';
import { BackendCutlistGroup } from '@cutr/constants/cutlist-backend';
import {
  MATERIAL_TYPE,
  MaterialType,
  SupplierMaterial,
} from '@cutr/constants/material';
import { SmallPartEdgebandingLimits } from '@cutr/constants/small-part-edgebanding';
import {
  defaultSheetEdgeTrimConfigAllSides,
  NestingInput,
  NestingOutput,
  Orientation,
  SheetConfig,
} from '@cutr/nesting';

import { getMaterial } from '@/api/materials';
import { isValidPart } from '@/api/store';
import { getCurrentFeatures } from '@/theme';

import {
  transformMaterialGroupToBackend,
  transformPartsToBackend,
} from './backend-compatibility';
import { SheetMaterials } from './edgeband';
import { isTruthy } from './misc';
import { getNestingDimensions } from './smallPartEdgebanding';

export type NestingGroup = {
  materialGroup: MaterialGroup;
  parts: PartItem[];
  smallPartEdgebandingLimits?: SmallPartEdgebandingLimits;
};

export function getSizes(sheetMaterials: SheetMaterials) {
  // if sheet materials have two core materials to glue and if both of them are melamine, then we trim 0.5mm from each.
  const melamineTypes = [
    MATERIAL_TYPE.melamine_chipboard,
    MATERIAL_TYPE.melamine_mdf,
  ] as MaterialType[];

  const shouldTrimThickness =
    sheetMaterials.core1 &&
    sheetMaterials.core2 &&
    melamineTypes.includes(sheetMaterials.core1?.type) &&
    melamineTypes.includes(sheetMaterials.core2?.type);

  const smallestRectangle = (materials: (Material | undefined)[]) => {
    const sizes = materials.filter(isTruthy).map((material) => ({
      width: material.widthMM,
      length: material.lengthMM,
      thicknessUM: material.thicknessUM,
    }));

    if (!sizes.length) {
      return { width: 0, length: 0, thickness: 0 };
    }

    const widths = sizes.map(({ width }) => width);
    const lengths = sizes.map(({ length }) => length);
    const thiccs = sizes.map(({ thicknessUM }) => thicknessUM);
    const width = Math.min(...widths);
    const length = Math.min(...lengths);
    const thickness = thiccs.reduce((tot, t) => (tot += t), 0);

    return {
      width,
      length,
      thickness: shouldTrimThickness ? thickness - 1000 : thickness,
    };
  };

  // We always keep the grain direction of the hpl layers unmodified (ie, along the length).
  // In some cases, it makes sense to rotate the core material 90 degrees to maximize the sheet size.
  // For example, if core material size is 1300x3050 and hpl size is 3050x1300, we would like to rotate the core material
  // and have a sheet size of 3050x1300, instead of gluing them as they are and resulting in sheet size of 1300x1300.
  // This logic picks the core material orientation that yields the largest area. In case if both orientations yield the same area,
  // it picks the one with the largest length.
  const rectangle1 = smallestRectangle([
    sheetMaterials.topHpl,
    sheetMaterials.core1,
    sheetMaterials.core2,
    sheetMaterials.bottomHpl,
  ]);
  // If it's only a core material, we don't rotate it.
  if (!sheetMaterials.topHpl && !sheetMaterials.bottomHpl) return rectangle1;

  const rectangle2 = smallestRectangle([
    sheetMaterials.topHpl,
    sheetMaterials.core1 // rotate core materials
      ? {
          ...sheetMaterials.core1,
          widthMM: sheetMaterials.core1.lengthMM,
          lengthMM: sheetMaterials.core1.widthMM,
        }
      : undefined,
    sheetMaterials.core2
      ? {
          ...sheetMaterials.core2,
          widthMM: sheetMaterials.core2.lengthMM,
          lengthMM: sheetMaterials.core2.widthMM,
        }
      : undefined,
    sheetMaterials.bottomHpl,
  ]);

  if (
    rectangle1.length * rectangle1.width >
    rectangle2.length * rectangle2.width
  )
    return rectangle1;

  if (
    rectangle1.length * rectangle1.width <
    rectangle2.length * rectangle2.width
  )
    return rectangle2;

  if (rectangle1.length > rectangle2.length) return rectangle1;
  return rectangle2;
}

export function getSheetDimensions(group: MaterialGroup) {
  const { core1, core2, topHpl, bottomHpl } = group;

  let { thickness, width, length } = getSizes({
    core1: getMaterial(core1),
    core2: getMaterial(core2),
    topHpl: getMaterial(topHpl),
    bottomHpl: getMaterial(bottomHpl),
  });

  let lengthWithoutSafetyMargins = length;

  lengthWithoutSafetyMargins -=
    group.sheetEdgeTrimConfig?.trimThickness.width1TrimThicknessMM || 0;
  lengthWithoutSafetyMargins -=
    group.sheetEdgeTrimConfig?.trimThickness.width2TrimThicknessMM || 0;
  width -= group.sheetEdgeTrimConfig?.trimThickness.length1TrimThicknessMM || 0;
  width -= group.sheetEdgeTrimConfig?.trimThickness.length2TrimThicknessMM || 0;

  return {
    thickness,
    width,
    length: lengthWithoutSafetyMargins,
    fullLength: length,
  };
}

export function partFitsMaterial(
  part: PartItem,
  partOrientation: Orientation,
  material: SupplierMaterial
): boolean {
  // This function is very similar to partFitsSheet.
  // We can't use partFitsSheet here due to sheet configs, etc, but probably we can merge them in the future.
  const partHasGrain = part.grainDirection === 'along';
  const materialHasGrain =
    material.hasGrainDirection || material.hasTextureDirection;

  if (partOrientation === 'vertical' && partHasGrain && materialHasGrain)
    return false;

  const partLength =
    partOrientation === 'horizontal' ? part.lengthMM : part.widthMM;
  const partWidth =
    partOrientation === 'horizontal' ? part.widthMM : part.lengthMM;

  return partLength <= material.lengthMM && partWidth <= material.widthMM;
}

export function getSmallestFittingMaterial(
  parts: PartItem[],
  automaticSheetSizeMaterials: SupplierMaterial['articleCode'][]
) {
  // if no parts (ie, empty group), return the first sheet size to get started
  if (!parts.length) return automaticSheetSizeMaterials[0];
  const smallestFittingMaterial = automaticSheetSizeMaterials
    .map(getMaterial)
    .filter(isTruthy)
    .find((material) =>
      parts.every(
        (part) =>
          partFitsMaterial(part, 'horizontal', material) ||
          partFitsMaterial(part, 'vertical', material)
      )
    );

  if (smallestFittingMaterial) return smallestFittingMaterial.articleCode;

  // none of the materials fit, return the last (largest) one
  return automaticSheetSizeMaterials[automaticSheetSizeMaterials.length - 1];
}

export function groupPartsByMaterial(parts: PartItem[]) {
  const groupMap = new Map<string, PartItem[]>();
  parts.filter(isValidPart).forEach((part) => {
    const materials = [part.core1, part.core2, part.topHpl, part.bottomHpl];

    if (!Object.values(materials).filter(Boolean).length) return;

    const uniqueId = Object.values(materials).join(':');

    if (!groupMap.has(uniqueId)) {
      groupMap.set(uniqueId, []);
    }

    const data = groupMap.get(uniqueId) as PartItem[];

    data.push(part);
  });

  return groupMap;
}

export function groupPartsForNesting(
  parts: PartItem[],
  groups: MaterialGroup[]
) {
  const isMaterialGroupValid = (group: MaterialGroup) =>
    [group.core1, group.core2, group.topHpl, group.bottomHpl].filter(Boolean)
      .length > 0;

  return groups.filter(isMaterialGroupValid).reduce(
    (acc, materialGroup) => {
      const validParts = parts
        .filter((part) => part.groupId === materialGroup.id)
        .filter(isValidPart);

      if (!validParts.length) return acc;

      if (!acc[materialGroup.id])
        acc[materialGroup.id] = { materialGroup, parts: [] };

      acc[materialGroup.id].parts = [
        ...acc[materialGroup.id].parts,
        ...validParts,
      ];

      return acc;
    },
    {} as Record<string, NestingGroup>
  );
}

function buildNestingParts(nestingGroup: NestingGroup) {
  const { stripSafetyMargins } = getCurrentFeatures();

  return (
    nestingGroup.parts
      .map((part: PartItem) => ({
        id: part.id,
        name: part.label,
        ...getNestingDimensions(part, nestingGroup.smallPartEdgebandingLimits),
        quantity: +part.quantity,
        hasGrain: part.grainDirection === 'along' ? true : false,
        partType: part.partType,
        shortId: part.shortId ?? '',
      }))
      // subtract the safety margin for strips so nesting engine can fit the parts
      .map((part) => {
        if (
          stripSafetyMargins === 'full-length-strips' &&
          part.partType === 'strip'
        ) {
          return {
            ...part,
            lengthMM:
              part.lengthMM -
              2 *
                (nestingGroup.materialGroup.sheetEdgeTrimConfig?.trimThickness
                  .width1TrimThicknessMM || 0),
          };
        }
        return part;
      })
  );
}

export function convertGroupToInput(nestingGroup: NestingGroup): NestingInput {
  const core1 = getMaterial(nestingGroup.materialGroup.core1);
  const core2 = getMaterial(nestingGroup.materialGroup.core2);
  const topHpl = getMaterial(nestingGroup.materialGroup.topHpl);
  const bottomHpl = getMaterial(nestingGroup.materialGroup.bottomHpl);

  const { width, length, thickness } = getSizes({
    core1,
    core2,
    topHpl,
    bottomHpl,
  });

  const sheetConfig: SheetConfig = {
    lengthMM: length,
    widthMM: width,
    thicknessUM: thickness,
    hasGrain: true,
    sheetEdgeTrimConfig:
      nestingGroup.materialGroup.sheetEdgeTrimConfig ||
      defaultSheetEdgeTrimConfigAllSides,
    maxHeightPerPacketMM: core1?.maxHeightPerPacketMM || 0,
  };

  return { sheetConfig, parts: buildNestingParts(nestingGroup) };
}

export function buildCutlistGroupForCandidate(
  nestingGroup: NestingGroup,
  candidate: { articleCode: string; output: NestingOutput }
): BackendCutlistGroup {
  const materialGroupForCandidate: MaterialGroup = {
    ...nestingGroup.materialGroup,
    core1: candidate.articleCode,
  };

  return {
    materialGroup: transformMaterialGroupToBackend(
      materialGroupForCandidate,
      0,
      candidate.output
    ),
    parts: transformPartsToBackend(nestingGroup.parts.filter(isValidPart)),
  } as BackendCutlistGroup;
}

export function convertGroupToInputForMaterial(
  nestingGroup: NestingGroup,
  material: Material
): NestingInput {
  const core2 = getMaterial(nestingGroup.materialGroup.core2);
  const topHpl = getMaterial(nestingGroup.materialGroup.topHpl);
  const bottomHpl = getMaterial(nestingGroup.materialGroup.bottomHpl);

  const { width, length, thickness } = getSizes({
    core1: material,
    core2,
    topHpl,
    bottomHpl,
  });

  const sheetConfig: SheetConfig = {
    lengthMM: length,
    widthMM: width,
    thicknessUM: thickness,
    hasGrain: true,
    sheetEdgeTrimConfig:
      nestingGroup.materialGroup.sheetEdgeTrimConfig ||
      defaultSheetEdgeTrimConfigAllSides,
    maxHeightPerPacketMM: material.maxHeightPerPacketMM || 0,
  };

  return { sheetConfig, parts: buildNestingParts(nestingGroup) };
}
